import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type GridApi, type IRowNode, type RowClassParams } from "ag-grid-community";
import type { StateUpdatedEvent, GridReadyEvent, CellEditingStoppedEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button, FormControl, MenuItem, Select, Tooltip } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";
import { useSSE, type ErrorEvent } from "@modules/sse";

import gridInputCellEditor from "@/components/aggrid/aggrid_input_cell_editor";
import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./inventory_audit_detail.scoped.scss";

interface State {
  gridApi: GridApi | null,
  stockCheckNo: string,
  workStationId: string
}

const defState = {
  gridApi: null,
  stockCheckNo: "",
  workStationId: ""
};

interface SearchParams {
  export: string,
  page: number,
  pageSize: number
}

const defSearchParams: SearchParams = {
  export: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Info {
  stockCheckNo: string,
  checkStatus: string,
  targetSkuQty: string,
  targetItemQty: string,
  checkSkuQty: string,
  checkItemQty: string,
  actTargetQty: string,
  progRate: string,
  detailInfos: any[],
  equipWorkInfos: any[]
}

const defInfo = {
  stockCheckNo: "",
  checkStatus: "",
  targetSkuQty: "",
  targetItemQty: "",
  checkSkuQty: "",
  checkItemQty: "",
  actTargetQty: "",
  progRate: "",
  detailInfos: [],
  equipWorkInfos: []
};

interface SkuInfo {
  skuNo: string,
  skuNoEx: string
}

const defSkuInfo = {
  skuNo: "",
  skuNoEx: ""
};

const validationMsg4reIn = [
  {id: "skuNo", type: "NotFound", msg: "재입고할 SKU가 없습니다."},
  {id: "workStationId", type: "NotBlank", msg: "작업대를 선택해 주세요."}
];

const validationMsg4reqSkuPick = [
  {id: "reqInfos", type: "NotBlank", msg: "지정출고 SKU를 선택해 주세요."},
  {id: "workStationId", type: "NotBlank", msg: "작업대를 선택해 주세요."},
  {id: "skuNo", type: "InvalidStatus", msg: "현재 작업대에서 호출할 수 없는 SKU입니다."}
];

const defColDef = { ...kutil.defaultColDef, cellDataType: false };

function InventoryAuditDetail(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [info, setInfo] = useState<Info>(kutil.deepCopy(defInfo));
  const [skuInfo, setSkuInfo] = useState<SkuInfo>(kutil.deepCopy(defSkuInfo));
  const [currSkuNo, setCurrSkuNo] = useState<string>("");

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const isActiveTab = useTabsStore((state) => state.currTabId == props.id);
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);
  const gridState4equip = useMemo(() => useSettingsStore.getState().getGridState(location.path + ".equip"), [location.path]);

  const skuInfoRef = useRef(skuInfo);
  skuInfoRef.current = skuInfo;
  const scrolledSkuRef = useRef("");

  const onSSEMessageWorkStations = useCallback((msg: MessageEvent) => {
    try {
      const result = JSON.parse(msg.data);
      const workStations = result.data;
      setCurrSkuNo(workStations.find((info: any) => info.workStationId === state.workStationId)?.skuNoEx || "");
    } catch {
      console.error("SSE parse 실패:", msg.data);
    }
  }, [state?.workStationId]);

  const onSSEError = useCallback((err: ErrorEvent) => {
    if (err.code == 400 || err.code == 401 || err.code == 403) {
        kutil.alert("로그인이 필요합니다.");
        kutil.removeRefreshToken();
        window.location.replace(window.location.href);
    }
    console.log(err);
  }, []);

  useSSE({
    url: "/api/sse/subscribe?eventName=work-stations&eventKey=",
    sessionId: useAuthStore.getState().sessionId,
    enabled: isActiveTab,
    messageHandlers: {
      "work-stations": onSSEMessageWorkStations,
    },
    onError: onSSEError,
  });

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "Location", field: "locationId", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.locationId },
    { headerName: "SKU No", field: "skuNoEx", width: 100, cellStyle: {textAlign: "center"} },
    { headerName: "관리구분", field: "mngType", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("mngType") },
    { headerName: "창고", field: "wh", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 160, cellStyle: { textAlign: "left"} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: { textAlign: "left"} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, cellStyle: { textAlign: "left"} },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 150, cellStyle: { textAlign: "left"} },
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 120, cellStyle: {textAlign: "left"} },
    { headerName: "Stock Qty", headerClass: "hd-ta-right", field: "stockQty", width: 85, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number },
    { headerName: "Check Qty", headerClass: "hd-ta-right", field: "checkQty", width: 85, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number,
      cellClass: ["input-cell", "ta-right"],
      editable: true, singleClickEdit:true,
      cellEditor: gridInputCellEditor,
      cellEditorParams: { maxLength: 7 },
    },
    { headerName: "Diff Qty", field: "diffQty", headerClass: "hd-ta-right", width: 85, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center"} },
    { headerName: "실사자", field: "checkUserName", width: 100, cellStyle: { textAlign: "center"} },
    { headerName: "Remark", headerClass: "hd-ta-left", field: "remark", width: 300, cellStyle: {textAlign: "left"},
      cellClass: ["input-cell", "ta-left"],
      editable: true, singleClickEdit:true,
      cellEditor: gridInputCellEditor,
      cellEditorParams: { type: "text", maxLength: 1000 }
    }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    getRowId: (p: any) => String(p.data.stockCheckDetailNo),
    getRowStyle: (p: RowClassParams) => {
      const skuNoEx = skuInfoRef.current?.skuNoEx;
      if (skuNoEx && p.data.skuNoEx == skuNoEx) {
        return { backgroundColor: "#fff1f1" };
      }
    },
    rowSelection: {
      mode: "multiRow" as const,
      isRowSelectable: (n: IRowNode) => {
        return (
          (n.data.outPossYn == "Y")
        );
      },
      hideDisabledCheckboxes: true
    },
    selectionColumnDef: {
      resizable: true
    },
    stopEditingWhenCellsLoseFocus: true
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
    onCellEditingStopped: (p: CellEditingStoppedEvent) => {
      if (p.newValue === p.oldValue) return;

      const field = p.colDef.field || "";
      const newValue = p.newValue;

      let updates: any = { [field]: newValue };
      if (field === "checkQty") {
        const newDiffQty = (p.data.stockQty != null && p.data.stockQty !== "" && newValue != null && newValue !== "") ? Number(newValue) - Number(p.data.stockQty) : null;
        updates.diffQty = newDiffQty;
      }

      setInfo(prev => ({...prev, detailInfos: prev.detailInfos.map((item: any) => item.stockCheckDetailNo === p.data.stockCheckDetailNo ? { ...item, ...updates } : item)}));
    }
  });

  const gridInfo4equip = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "Track No", field: "workNoMfc", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "작업구분", field: "workType", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("workType") },
    { headerName: "창고", field: "wh", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 160, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Job Qty", headerClass: "hd-ta-right", field: "jobQty", width: 100, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number },
    { headerName: "작업대", field: "workStationId", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("workStationId") },
    { headerName: "SKU No", field: "skuNoEx", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Location", field: "startPos", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.locationId, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "설비", field: "workEquip", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("workEquip") },
    { headerName: "설비상태", field: "workStatusEquip", width: 100, cellStyle: {textAlign: "center"},
      valueGetter: (p: any) => {
        return (p.data.workStatusEquip == "START" || p.data.workStatusEquip == "ACTIVE" ? p.data.currPos : commonCode.getCodeValue("workStatusEquip", p.data.workStatusEquip));
      }
    }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridCallbacks4equip = {
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path + ".equip");
    }
  };

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  // 바코드 스캔 기능, 단축키 적용
  const onBarcodeScanRef = useRef(onBarcodeScan);
  const onKeyboardShortcutRef = useRef(onKeyboardShortcut);
  useEffect(() => {
    onBarcodeScanRef.current = onBarcodeScan;
    onKeyboardShortcutRef.current = onKeyboardShortcut;
  });

  useEffect(() => {
    if (!isActiveTab) {
      return;
    }

    // 바코드 스캔 핸들러
    const barcodeHandler = (code: string) => onBarcodeScanRef.current(code);
    kutil.startBarcodeScan(barcodeHandler);

    // 키보드 단축키 핸들러
    const shortcuthandler = (code: string) => onKeyboardShortcutRef.current(code);
    kutil.startKeyboardShortcut([
      {id: "F1", altKey: false, ctrlKey: false, shiftKey: false, key: "F1"},
      {id: "F2", altKey: false, ctrlKey: false, shiftKey: false, key: "F2"},
      {id: "F3", altKey: false, ctrlKey: false, shiftKey: false, key: "F3"},
    ], shortcuthandler);

    return () => {
      kutil.stopBarcodeScan(barcodeHandler)
      kutil.stopKeyboardShortcut(shortcuthandler)
    };
  }, [isActiveTab]);
  // 끝: 바코드 스캔 기능 적용

  // 변경 작업대ID 저장
  useEffect(() => {
    if (state.workStationId) {
      useSettingsStore.getState().setLocalSetting("workStationId", location.path, state.workStationId);
    }
  }, [state.workStationId]);

  useEffect(() => {
    if (state.workStationId) {
      searchInfo();
    }
  }, [state.workStationId, currSkuNo]);

  useEffect(() => {
    const api = state.gridApi;
    const skuNoEx = skuInfo?.skuNoEx || "";

    if (!skuNoEx) {
      scrolledSkuRef.current = "";
      return;
    }
    if (!api || info.detailInfos.length == 0) return;
    if (scrolledSkuRef.current == skuNoEx) return;

    let target: IRowNode | undefined;
    api.forEachNodeAfterFilterAndSort((n) => {
      if (!target && n.data?.skuNoEx == skuNoEx) target = n;
    });
    if (!target || target.rowIndex == null) return;

    api.ensureNodeVisible(target, "middle");
    scrolledSkuRef.current = skuNoEx;
  }, [skuInfo, info.detailInfos, state.gridApi]);

  const isInit = useRef(false);
  async function init() {
  try {
    if (isInit.current == false) {
      isInit.current = await initData();
    }
    if (isInit.current) {
      await initParams();
      await searchInfo();
    }
  } finally {
    kutil.setLoadingStatus(false);
  }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const query = Object.fromEntries(new URLSearchParams(location.search).entries());
    state.stockCheckNo = query.stockCheckNo || "";

    if (!state.workStationId) {
      const workStationId = useSettingsStore.getState().getDefWorkStationId(location.path)
        || useSettingsStore.getState().getWorkStationId(location.path)
        || (commonCode.workStationId as any[])?.[0]?.code
        || "";
      state.workStationId = workStationId ;
    }

    setState({ ...state });
  }

  async function searchInfo() {
    if (!state.stockCheckNo) {
      return;
    }

    try {
      const data = await kutil.sendGet("/api/stock/inventory_audit_detail.data?mode=search", {stockCheckNo: state.stockCheckNo, workStationId: state.workStationId}, {});
      const currSkuEx = data.skuInfo?.skuNoEx;
      const isEmpty = (v: any) => v == null || v === "";
      const detailInfos = (data.detailInfos ?? []).map((item: any) =>
        currSkuEx && item.skuNoEx == currSkuEx && isEmpty(item.checkQty) && !isEmpty(item.stockQty)
          ? { ...item, checkQty: item.stockQty, diffQty: 0 }
          : item
      );

      setSkuInfo(data.skuInfo);
      setInfo({
        ...data.info,
        detailInfos: detailInfos,
        equipWorkInfos: data.equipWorkInfos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function save() {
    if (await kutil.confirm("저장하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/stock/inventory_audit_detail.proc?mode=updateCheckQty", info, {
      });
      await kutil.alert("저장하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }
 
  async function reqSkuPick() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length == 0) {
      kutil.alert("지정출고 SKU를 선택해 주세요.");
      return;
    }

    if (!state.workStationId) {
      kutil.alert("작업대를 선택해 주세요.");
      return;
    }

    for (const info of selectedInfos) {
      info.workStationId = state.workStationId;
    }

    if (await kutil.confirm("지정출고하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/stock/inventory_audit_detail.proc?mode=reqSkuPick", selectedInfos, {
        validationMsg: validationMsg4reqSkuPick
      });
      await kutil.alert("지정출고하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function reIn() {
    const reqInfos = (info.detailInfos as any[]).filter(item => !item.checkUserName && item.skuNoEx == skuInfo.skuNoEx);
    const confirmMsg = reqInfos.length > 0 ? "실사 결과를 저장하지 않았습니다.\r\n" : "";
    if (await kutil.confirm(confirmMsg + "재입고하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/stock/inventory_audit_detail.proc?mode=reIn", {workStationId: state.workStationId}, {
        validationMsg: validationMsg4reIn
      });
      await kutil.alert("재입고하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function updateCheckStatus() {
    const confirmMsg = info.checkStatus == "C" ? "종료취소" : "실사종료";
    if (await kutil.confirm(confirmMsg + "하시겠습니까?") == false) {
      return false;
    }

    try {
      const reqInfo = { ...info, checkStatus: info.checkStatus === "C" ? "P" : "C" };
      setInfo(reqInfo);
      const data = await kutil.sendPost("/api/stock/inventory_audit_detail.proc?mode=updateCheckStatus", reqInfo, {
      });
      await kutil.alert(confirmMsg + "하였습니다.");
      goList();
    } catch(e) {
      console.log(e);
    }
  }

  async function remove() {
    if (await kutil.confirm("삭제하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/stock/inventory_audit_detail.proc?mode=remove", info, {
      });
      await kutil.alert("삭제하였습니다.");
      goList();
    } catch(e) {
      console.log(e);
    }
  }

  async function downloadExcel() {
    const searchParams = kutil.copyObj({stockCheckNo: state.stockCheckNo, workStationId: state.workStationId}, {} as SearchParams);
    searchParams.export = "excel";
    searchParams.page = 1;
    searchParams.pageSize = 10000;
    try {
      await kutil.downloadFile("/api/stock/inventory_audit_detail.download?mode=search", searchParams, {
        "fileName": "재고실사상세_" + kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss") + ".xlsx"
      });
    } catch(e) {
      console.log(e);
    }
  }

  function goList() {
    router.navigateByPath("/stock/inventory_audit_list", JSON.parse(decodeURI(router.query("searchParams") || "{}")));
  }

  async function onBarcodeScan(code: string) {
    const [prefix, ...rest] = code.split(":");
    const value = rest.join(":").trim();

    if (rest.length > 0) {
      switch (prefix) {
        case "WS": {
          if (!value) return;
          const exists = (commonCode.workStationId as any[]).some(c => c.code === value);
          if (!exists) {
            kutil.alert("일치하는 작업대가 없습니다.\r\n" + code);
            return;
          }
          setState(prev => ({ ...prev, workStationId: value }));
          return;
        }
        default:
          return;
      }
    }
  }

  async function onKeyboardShortcut(shortcut: string) {
    if (shortcut == "F1") {
      searchInfo();
    } else if (shortcut == "F2") {
      save();
    } else if (shortcut == "F3") {
      reIn();
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>재고실사</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Tooltip title="단축키 [F1]">
              <Button variant="outlined" onClick={searchInfo}>조회</Button>
            </Tooltip>
            <Button variant="outlined" onClick={goList}>목록</Button>
            { auth.download &&
            <Button variant="outlined" onClick={downloadExcel}>엑셀다운</Button>
            }
            { info.checkStatus != "C" && auth.delete &&
            <Button variant="outlined" onClick={remove}>삭제</Button>
            }
            { info.checkStatus == "C" && auth.func1 &&
            <Button variant="outlined" onClick={updateCheckStatus}>종료취소</Button>
            }
            { info.checkStatus != "C" && auth.write &&
            <Button variant="outlined" onClick={updateCheckStatus}>실사종료</Button>
            }
            { auth.write &&
            <Tooltip title="단축키 [F3]">
              <Button variant="outlined" onClick={reIn}>재입고</Button>
            </Tooltip>
            }
            { auth.write &&
            <Button variant="outlined" onClick={reqSkuPick}>지정출고</Button>
            }
            { info.checkStatus != "C" && auth.write &&
            <Tooltip title="단축키 [F2]">
              <Button variant="contained" onClick={save}>저장</Button>
            </Tooltip>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 그리드 상단 */}
        <div className="grid-top-area">
          <div>
            <div>
              <p>대상SKU :</p>
              <span>{ valueFormatter.number(info.targetSkuQty) }</span>
            </div>
            <em></em>
            <div>
              <p>대상ITEM :</p>
              <span>{ valueFormatter.number(info.targetItemQty) }</span>
            </div>
            <em></em>
            <div>
              <p>실사SKU :</p>
              <span>{ valueFormatter.number(info.checkSkuQty) }</span>
            </div>
            <em></em>
            <div>
              <p>실사ITEM :</p>
              <span>{ valueFormatter.number(info.checkItemQty) }</span>
            </div>
            <em></em>
            <div>
              <p>수량차이 :</p>
              <span>{ valueFormatter.number(info.actTargetQty) }</span>
            </div>
            <em></em>
            <div>
              <p>진척률 :</p>
              <span>{ valueFormatter.number(info.progRate) }%</span>
            </div>
          </div>
        </div>
        {/*// 그리드 상단 */}
        {/* 실사 정보 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={info.detailInfos}
              defaultColDef={defColDef}
              initialState={gridState}
              getRowId={gridOptions.getRowId}
              getRowStyle={gridOptions.getRowStyle}
              rowSelection={gridOptions.rowSelection}
              selectionColumnDef={gridOptions.selectionColumnDef}
              stopEditingWhenCellsLoseFocus={gridOptions.stopEditingWhenCellsLoseFocus}
              showInfo={false}
              onGridReady={gridCallbacks.onGridReady}
              onStateUpdated={gridCallbacks.onStateUpdated}
              onCellEditingStopped={gridCallbacks.onCellEditingStopped}
            />
          </div>
        </AgGridProvider>
        {/*// 실사 정보 */}
        {/* 서브 타이틀 */}
        <div className="sub-title-area">
          <p>작업대 작업 목록</p>
          <div className="workstation">
            <p>작업대</p>
            <FormControl size="small">
              <Select
                value={state.workStationId}
                onChange={(e) => {setState(prev => ({ ...prev, workStationId: e.target.value }))}}
                displayEmpty
              >
              {(commonCode.workStationId as any[]).map((code) => (
                <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
              ))}
              </Select>
            </FormControl>
          </div>
        </div>
        {/* 작업 목록 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo4equip}
              rowData={info.equipWorkInfos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState4equip}
              showInfo={false}
              onStateUpdated={gridCallbacks4equip.onStateUpdated}
            />
          </div>
        </AgGridProvider>
        {/*// 작업 목록 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(InventoryAuditDetail);
